import { escapeHtml } from '../ui.js';
import { listFolder, isFolderMime, sendMessage, downloadDriveFile, ensureScopedToken, getGmailSignature, searchProjectFolders } from '../google-api.js';
import { pickProjectFolder, walkFolder, isSupersededPath, folderCode as sharedFolderCode, folderHuman as sharedFolderHuman } from '../drive-folders.js';
import { allProjects, callFunction, allUsers, createMailApproval } from '../db.js';
import { logError } from '../telemetry.js';
import { holdingWithoutTime } from '../responsiveness.js';
import { buildMime, parseAddresses, sentSubject, htmlToText, MAX_ATTACH_BYTES } from '../mail-send.js';
import { isPerson } from '../people.js';

// MAIL — COMPOSE / REPLY (artboard 5d) + ATTACH-FROM-PROJECT (artboard 2c right panel).
// The point where writing joins filing: a message is always written against a
// JOB (the first field), and that job decides the signature, the file picker,
// and where the sent copy is filed. Reproduces 5d verbatim (760px card, inline
// --brand-* tokens) and the 2c 560px picker.
//
// SEND IS ON (Karan 06/10/2026, "Yes, for everyone"; it was off from 06/08).
// The message goes from the person's OWN Gmail with gmail.send, asked for once
// on their first Send. The subject carries TRACK// {project} so the archiver
// files the copy, and the thread is filed to the project in Track on send.
// Only a person pressing Send sends — Track Intelligence never does.
//
// House rendering-safety rule 0: every data value renders through escapeHtml();
// any data-built href goes through safeUrl(). Firestore/Gmail/Drive content and
// user input are treated as hostile.

// ---- tokens / small style consts ------------------------------------------
const MONO = 'font-family:var(--font-technical);';
const EYEBROW = 'font-size:10px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;';
const BACKDROP = 'position:fixed;inset:0;z-index:200;background:rgba(0,0,0,0.45);display:flex;align-items:flex-start;justify-content:center;padding:48px 24px;overflow:auto;';
const PICKER_BACKDROP = BACKDROP.replace('z-index:200', 'z-index:210');

// Category pills — the four grounded categories the 5d rail shows, plus Urgent
// as an orange-outline toggle. (mail.js carries a five-set that also includes
// 'Enquiries'; 5d's compose rail is these four, so we match the artboard.)
const CATEGORIES = ['Projects', 'Finance', 'Admin', 'FYI'];

// The binding numbering grammar (same as mail-thread.js / files.js):
// PROJECT-FOLDER-TYPEITEM_REV, e.g. 7014-05-0301_C.
const DOC_RE = /^(\d{4}|A\d{4})-(\d{2})-(\d{2})(\d{2})(?:_([A-Za-z]\d*|[A-Za-z]))?$/;
const JOB_RE = /^(\d{4}|A\d{4})$/i;
const CAD_EXT = /\.(dwg|dxf|dwf|dgn)$/i;
const PDF_EXT = /\.pdf$/i;

// ---- pure helpers ---------------------------------------------------------
const pad = (n) => String(n).padStart(2, '0');
function hhmm(d) { const x = d instanceof Date ? d : new Date(d); return (x && !isNaN(x)) ? `${pad(x.getHours())}:${pad(x.getMinutes())}` : '—'; }
function whenFull(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (!d || isNaN(d)) return '—';
  const now = new Date();
  const same = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return same ? `today ${hhmm(d)}` : `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${hhmm(d)}`;
}
function fmtSize(n) {
  n = Number(n) || 0;
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}
function stemOf(name) { const s = String(name || ''); const i = s.lastIndexOf('.'); return i > 0 ? s.slice(0, i) : s; }
function extOf(name) { const s = String(name || ''); const i = s.lastIndexOf('.'); return i > 0 ? s.slice(i) : ''; }
function parseDocNumber(name) {
  const m = stemOf(name).match(DOC_RE);
  if (!m) return null;
  return { project: m[1], folder: m[2], type: m[3], item: m[4], rev: m[5] || '' };
}
// The shared reading of a folder number (drive-folders.js): ASP's folders are
// "4. Calculations", and this file's own copy wanted two digits, so the
// project picker found nothing on every project (08/10/2026).
const folderCode = sharedFolderCode;
const folderHuman = sharedFolderHuman;
const projName = (p) => (p ? (p.title || p.clientName || '') : '');
const senderNameOf = (m) => (m && m.from && (m.from.name || m.from.email)) || 'someone';

// Strip a leading "Re:"/"Fwd:" run so we don't stack prefixes.
function bareSubject(s) { return String(s || '').replace(/^\s*(re|fwd|fw)\s*:\s*/i, '').trim(); }

// ---- marks (navy stroke, 26-grid; from 2c/4d) -----------------------------
const markDrawing = () => '<svg width="15" height="15" viewBox="0 0 26 26" fill="none" stroke="var(--ink)" stroke-width="2.4" style="flex:none;"><rect x="5" y="3" width="16" height="20" rx="1"></rect><path d="M5 17 L21 6"></path></svg>';
const markFile = () => '<svg width="15" height="15" viewBox="0 0 26 26" fill="none" stroke="var(--ink)" stroke-width="2.4" style="flex:none;"><rect x="5" y="3" width="16" height="20" rx="1"></rect><path d="M9 10h8M9 14h8M9 18h4"></path></svg>';

// ===========================================================================
// COMPOSE (5d)
// ===========================================================================
export function renderCompose(session, opts = {}) {
  const mode = (opts.mode === 'reply' || opts.mode === 'forward') ? opts.mode : 'new';
  const now = new Date();

  // Whether the job is fixed. Reply / forward / from-project all arrive with a
  // job and lock it; only a cold "new" compose (no job) lets you type one.
  const jobLocked = !!opts.job;

  // ---- the draft model ----------------------------------------------------
  // No real draft persistence this pass — the "Saved as draft HH:MM" stamp is
  // static and nothing is written anywhere; wire persistence when send lands.
  const draft = {
    job: opts.job ? String(opts.job).toUpperCase() : '',
    project: opts.project || null,
    to: opts.to || '',
    cc: '',
    bcc: '',
    category: 'Projects',
    urgent: false,
    subject: mode === 'reply' ? `Re: ${bareSubject(opts.subject)}`
      : mode === 'forward' ? `Fwd: ${bareSubject(opts.subject)}`
        : (opts.subject || ''),
    // attachments: { kind:'device'|'project', name, size, docNumber } — display
    // only this pass (no bytes move; nothing is sent).
    attachments: [],
  };
  // A reply stays in its Gmail thread: the thread id and the RFC Message-Id of
  // the newest message (getThreadFull carries both).
  const lastMsg = Array.isArray(opts.thread) && opts.thread.length ? opts.thread[opts.thread.length - 1] : null;
  const replyCtx = mode === 'reply' && lastMsg ? {
    threadId: opts.threadId || lastMsg.threadId || '',
    inReplyTo: lastMsg.rfcId || '',
    references: [lastMsg.references, lastMsg.rfcId].filter(Boolean).join(' '),
  } : { threadId: '', inReplyTo: '', references: '' };

  const eyebrow = mode === 'reply' ? 'Reply' : mode === 'forward' ? 'Forward' : 'New message';

  // Signature. Name from the session; role from the first assigned role; firm +
  // phone are placeholders until org settings expose them.
  const sigName = (session && session.displayName) || (session && session.email) || 'ASP';
  const sigRole = (session && Array.isArray(session.roles) && session.roles[0]) || '';
  // signature phone/firm: placeholder — wire to org settings when available.
  const SIG_FIRM = 'ASP';
  const SIG_PHONE = '+44 (0) 1530 561230';

  // ---- overlay scaffold ---------------------------------------------------
  const overlay = document.createElement('div');
  overlay.setAttribute('style', BACKDROP);
  document.body.appendChild(overlay);
  const close = () => { overlay.remove(); document.removeEventListener('keydown', onKey, true); };
  function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); close(); } }
  document.addEventListener('keydown', onKey, true);
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });

  // Reply/forward quoting (opts.thread = the thread's messages, chronological).
  const quoted = quotedBodyHtml(mode, opts.thread);

  const inputReset = 'border:none;background:transparent;font-family:inherit;';

  // NOTE: 5d is drawn as a 760px card centred inside a 1000px frame. Presented
  // here directly on the modal backdrop (the card carries its own header/footer
  // chrome, so openOverlay's title bar would double up — the file-to-project
  // dialog uses this same self-contained-card pattern).
  overlay.innerHTML = `
    <div style="width:760px;max-width:100%;background:var(--surface);border:1px solid var(--hair);border-radius:16px;overflow:hidden;display:flex;flex-direction:column;">

      <div style="padding:18px 22px;background:var(--brief-bg);border-bottom:1px solid var(--hair);display:flex;align-items:center;justify-content:space-between;">
        <span style="${EYEBROW}color:var(--brand-orange);">${escapeHtml(eyebrow)}</span>
        <span style="${MONO}font-size:11px;color:var(--ink-3);">Saved as draft ${escapeHtml(hhmm(now))}</span>
      </div>

      <div style="padding:4px 22px;display:flex;flex-direction:column;">

        <div style="padding:14px 0;border-bottom:1px solid var(--hair);display:flex;align-items:center;gap:14px;">
          <span style="width:66px;flex:none;font-size:12px;letter-spacing:0.1em;text-transform:uppercase;color:var(--ink-3);">Project</span>
          <span id="cmp-jobslot" style="flex:1;min-width:0;display:flex;align-items:center;gap:10px;"></span>
        </div>

        <div style="padding:14px 0;border-bottom:1px solid var(--hair);display:flex;align-items:baseline;gap:14px;">
          <span style="width:66px;flex:none;font-size:12px;letter-spacing:0.1em;text-transform:uppercase;color:var(--ink-3);">To</span>
          <input id="cmp-to" type="text" value="${escapeHtml(draft.to)}" placeholder="name@example.com" style="${inputReset}flex:1;font-size:14px;color:var(--ink);" />
          <span id="cmp-ccbcc" style="font-size:12.5px;color:var(--link);flex:none;cursor:pointer;">Cc Bcc</span>
        </div>

        <div id="cmp-ccrow" style="display:none;padding:14px 0;border-bottom:1px solid var(--hair);flex-direction:column;gap:10px;">
          <div style="display:flex;align-items:baseline;gap:14px;">
            <span style="width:66px;flex:none;font-size:12px;letter-spacing:0.1em;text-transform:uppercase;color:var(--ink-3);">Cc</span>
            <input id="cmp-cc" type="text" placeholder="cc@example.com" style="${inputReset}flex:1;font-size:14px;color:var(--ink);" />
          </div>
          <div style="display:flex;align-items:baseline;gap:14px;">
            <span style="width:66px;flex:none;font-size:12px;letter-spacing:0.1em;text-transform:uppercase;color:var(--ink-3);">Bcc</span>
            <input id="cmp-bcc" type="text" placeholder="bcc@example.com" style="${inputReset}flex:1;font-size:14px;color:var(--ink);" />
          </div>
        </div>

        <div style="padding:14px 0;border-bottom:1px solid var(--hair);display:flex;align-items:baseline;gap:14px;">
          <span style="width:66px;flex:none;font-size:12px;letter-spacing:0.1em;text-transform:uppercase;color:var(--ink-3);">Category</span>
          <span id="cmp-cats" style="display:flex;gap:7px;flex-wrap:wrap;"></span>
        </div>

        <div style="padding:16px 0;border-bottom:1px solid var(--hair);display:flex;flex-direction:column;gap:8px;">
          <div style="display:flex;align-items:baseline;gap:14px;">
            <span style="width:66px;flex:none;font-size:12px;letter-spacing:0.1em;text-transform:uppercase;color:var(--ink-3);">Subject</span>
            <input id="cmp-subject" type="text" value="${escapeHtml(draft.subject)}" placeholder="Subject" style="${inputReset}flex:1;font-size:16px;font-weight:500;letter-spacing:-0.015em;color:var(--ink);" />
          </div>
          <span id="cmp-sentas" style="padding-left:80px;font-size:11.5px;color:var(--ink-3);"></span>
        </div>

      </div>

      <div style="padding:20px 22px;display:flex;flex-direction:column;gap:18px;min-height:180px;">
        <div id="cmp-body" contenteditable="true" style="font-size:15px;line-height:1.65;max-width:76ch;min-height:120px;color:var(--ink);text-wrap:pretty;">${opts.body ? escapeHtml(String(opts.body)).replace(/\n/g, "<br>") : ""}${quoted}</div>
        <div id="cmp-sig" style="display:flex;flex-direction:column;gap:6px;padding-top:6px;border-top:1px solid var(--hair);">
          <span style="${EYEBROW}color:var(--ink-3);">Signature \u00b7 reading from Gmail\u2026</span>
        </div>
      </div>

      <div id="cmp-attachments" style="display:none;padding:0 22px 16px;flex-wrap:wrap;gap:10px;"></div>

      <div style="padding:16px 22px;background:var(--brief-bg);border-top:1px solid var(--hair);display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
        <span id="cmp-send" style="background:var(--ink-fill);color:var(--ink-fill-ink);border-radius:8px;padding:11px 20px;font-size:13.5px;font-weight:500;cursor:pointer;">Send</span>
        <span id="cmp-ask" role="button" tabindex="0" style="border:1px solid var(--hair-strong);border-radius:8px;padding:11px 16px;font-size:13px;cursor:pointer;">Ask for approval</span>
        <span id="cmp-attach-proj" style="border:1px solid var(--hair-strong);border-radius:8px;padding:11px 16px;font-size:13px;cursor:pointer;">Attach from project</span>
        <span id="cmp-attach-dev" style="border:1px solid var(--hair-strong);border-radius:8px;padding:11px 16px;font-size:13px;cursor:pointer;">Attach from this device</span>
        <input id="cmp-device" type="file" multiple style="display:none;" />
        <div style="flex:1;"></div>
        <span style="font-size:12px;color:var(--ink-3);">Filed to <span id="cmp-filed" style="${MONO}color:var(--ink);"></span> on send</span>
      </div>

      <div id="cmp-askpanel" class="cmp-askpanel" hidden></div>

      <div id="cmp-note" style="padding:0 22px 16px;font-size:12.5px;color:var(--ink-3);min-height:0;"></div>

    </div>`;

  const card = overlay.firstElementChild;
  const noteEl = card.querySelector('#cmp-note');
  const showNote = (msg, tone = 'var(--ink-3)') => { noteEl.textContent = msg; noteEl.style.color = tone; };

  // ---- shared project cache for cold-new job resolution -------------------
  let projectsPromise = null;
  const loadProjects = () => (projectsPromise || (projectsPromise = allProjects().catch(() => [])));

  // ---- job slot -----------------------------------------------------------
  function renderJobSlot() {
    const slot = card.querySelector('#cmp-jobslot');
    if (draft.job && draft.project) {
      slot.innerHTML = `
        <span style="display:inline-flex;align-items:center;gap:10px;background:var(--brief-bg);border-radius:8px;padding:7px 12px;">
          <span style="${MONO}font-size:13.5px;font-weight:500;">${escapeHtml(draft.job)}</span>
          <span style="font-size:13.5px;">${escapeHtml(projName(draft.project))}</span>
          ${jobLocked ? '' : '<span id="cmp-job-clear" style="font-size:12px;color:var(--ink-3);cursor:pointer;">&#10005;</span>'}
        </span>`;
      const clr = slot.querySelector('#cmp-job-clear');
      if (clr) clr.addEventListener('click', () => { draft.job = ''; draft.project = null; renderJobSlot(); syncJobDependents(); });
    } else if (draft.job && !draft.project) {
      // Typed a job number that resolved to no project.
      slot.innerHTML = `
        <span style="display:inline-flex;align-items:center;gap:10px;background:var(--brief-bg);border-radius:8px;padding:7px 12px;">
          <span style="${MONO}font-size:13.5px;font-weight:500;">${escapeHtml(draft.job)}</span>
          <span style="font-size:12.5px;color:var(--brand-orange);">no project matches that number</span>
          ${jobLocked ? '' : '<span id="cmp-job-clear" style="font-size:12px;color:var(--ink-3);cursor:pointer;">&#10005;</span>'}
        </span>`;
      const clr = slot.querySelector('#cmp-job-clear');
      if (clr) clr.addEventListener('click', () => { draft.job = ''; draft.project = null; renderJobSlot(); syncJobDependents(); });
    } else if (!jobLocked) {
      // Cold "new" compose with no job: type a 4-digit / A-number, or the dashed
      // "no job" affordance stands in until one is entered.
      slot.innerHTML = `
        <input id="cmp-job-in" type="text" placeholder="7017" maxlength="5" style="border:1px dashed var(--brand-orange);border-radius:8px;padding:7px 12px;${MONO}font-size:13.5px;font-weight:500;width:88px;color:var(--ink);background:var(--surface);" />
        <span style="font-size:12.5px;color:var(--ink-3);">Type a project number — it sets the signature, the file picker and where the sent copy is filed.</span>`;
      const inp = slot.querySelector('#cmp-job-in');
      const tryResolve = async () => {
        if (!inp.isConnected) return;   // Enter already resolved it; this is the blur as the input went
        const raw = inp.value.trim().toUpperCase();
        if (!JOB_RE.test(raw)) { if (raw) showNote('A project number is four digits (7017) or an A-number (A0209).', 'var(--brand-orange)'); return; }
        showNote('');   // a stale "four digits" warning must not outlive a valid number
        draft.job = raw;
        const projects = await loadProjects();
        draft.project = projects.find((p) => String(p.number) === String(raw)) || null;
        renderJobSlot();
        syncJobDependents();
      };
      inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); tryResolve(); } });
      inp.addEventListener('blur', tryResolve);
      inp.focus();
    } else {
      slot.innerHTML = `<span style="width:30px;height:18px;border:1px dashed var(--brand-orange);border-radius:4px;flex:none;"></span>
        <span style="font-size:12.5px;color:var(--ink-3);">No project — assign one to file this on send.</span>`;
    }
  }

  // Things that depend on the job: the "Sent as: TRACK// …" note and the "Filed
  // to {job} on send" stamp.
  function syncJobDependents() { renderSentAs(); renderFiled(); }

  function renderSentAs() {
    const el = card.querySelector('#cmp-sentas');
    // On send (later) the subject is prefixed 'TRACK// {job}' so the archiver
    // files the sent copy to the project (Karan 06/08). Surfaced here so the
    // writer sees the tag that will be prepended; nothing is sent this pass.
    const subj = draft.subject || '{subject}';
    if (draft.job) el.textContent = `Sent as: TRACK// ${draft.job} — ${subj}`;
    else el.textContent = 'Sent as: TRACK// {project} — assign a project to file the sent copy.';
  }
  function renderFiled() {
    const el = card.querySelector('#cmp-filed');
    el.textContent = draft.job || '—';
  }

  // ---- category pills -----------------------------------------------------
  function renderCats() {
    const wrap = card.querySelector('#cmp-cats');
    const pills = CATEGORIES.map((c) => {
      const on = draft.category === c;
      const style = on
        ? 'background:var(--ink-fill);color:var(--ink-fill-ink);border:1px solid var(--ink);'
        : 'border:1px solid var(--hair);color:var(--ink);';
      return `<span class="cmp-cat" data-cat="${escapeHtml(c)}" style="${style}border-radius:999px;padding:5px 12px;font-size:11.5px;cursor:pointer;">${escapeHtml(c)}</span>`;
    }).join('');
    const urgentStyle = draft.urgent
      ? 'background:var(--brand-orange);color:var(--brand-white);border:1px solid var(--brand-orange);'
      : 'border:1px solid var(--brand-orange);color:var(--brand-orange);';
    const urgent = `<span id="cmp-urgent" style="${urgentStyle}border-radius:999px;padding:5px 12px;font-size:11.5px;cursor:pointer;">Urgent</span>`;
    wrap.innerHTML = pills + urgent;
    wrap.querySelectorAll('.cmp-cat').forEach((el) => el.addEventListener('click', () => { draft.category = el.dataset.cat; renderCats(); }));
    wrap.querySelector('#cmp-urgent').addEventListener('click', () => { draft.urgent = !draft.urgent; renderCats(); });
  }

  // ---- attachment chips ---------------------------------------------------
  function renderAttachments() {
    const wrap = card.querySelector('#cmp-attachments');
    if (!draft.attachments.length) { wrap.style.display = 'none'; wrap.innerHTML = ''; return; }
    wrap.style.display = 'flex';
    wrap.innerHTML = draft.attachments.map((a, i) => {
      const mark = (a.kind === 'project' && /\.(pdf)$/i.test(a.name)) || CAD_EXT.test(a.name) ? markDrawing() : markFile();
      const label = a.docNumber ? `<span style="${MONO}">${escapeHtml(a.docNumber)}</span>`
        : `<span>${escapeHtml(a.name)}</span>`;
      const meta = a.docNumber ? '<span style="color:var(--ink-3);">project file</span>'
        : (a.size ? `<span style="color:var(--ink-3);">${escapeHtml(fmtSize(a.size))}</span>` : '');
      return `<span style="border:1px solid var(--hair);border-radius:10px;padding:9px 13px;display:flex;align-items:center;gap:10px;font-size:12.5px;">
        ${mark}${label}${meta}
        <span class="cmp-chip-x" data-ix="${i}" style="color:var(--ink-3);cursor:pointer;">&#10005;</span>
      </span>`;
    }).join('');
    wrap.querySelectorAll('.cmp-chip-x').forEach((el) => el.addEventListener('click', () => {
      draft.attachments.splice(+el.dataset.ix, 1);
      renderAttachments();
    }));
  }

  function addAttachment(att) {
    // De-dupe project files by docNumber, device files by name+size.
    const dup = draft.attachments.some((a) => a.docNumber && att.docNumber
      ? a.docNumber === att.docNumber
      : (a.name === att.name && a.size === att.size && a.kind === att.kind));
    if (!dup) draft.attachments.push(att);
  }

  // ---- signature: the person's own, from Gmail (Karan 07/10/2026) ---------
  // Shown on white because an email signature is drawn for a white email, and
  // that is how the recipient will see it.
  let sigHtml = '';
  const fallbackSig = `<p>${escapeHtml(sigName)}<br>${escapeHtml([sigRole, SIG_FIRM].filter(Boolean).join(' \u00b7 '))}<br>${escapeHtml(SIG_PHONE)} \u00b7 aspconsulting.co.uk</p>`;
  const purify = (h) => (window.DOMPurify ? window.DOMPurify.sanitize(h) : '');
  getGmailSignature().then((html) => {
    const clean = purify(html || '');
    const el = card.querySelector('#cmp-sig');
    if (!el) return;
    sigHtml = clean || (html ? '' : fallbackSig);
    el.innerHTML = clean
      ? `<span style="${EYEBROW}color:var(--ink-3);">Your Gmail signature</span><div class="cmp-sig-paper">${clean}</div>`
      : `<span style="${EYEBROW}color:var(--brand-orange);">No signature in Gmail</span><span style="font-size:12.5px;color:var(--ink-2);">This will go with a plain name block instead. Set your signature in Track\u2019s signature settings, or in Gmail.</span><div class="cmp-sig-paper">${fallbackSig}</div>`;
  }).catch((e) => {
    sigHtml = fallbackSig;
    const why = (e && e.message) || 'unknown error';
    logError('mail.signature', why, e && e.stack);   // so a browser-specific failure reaches the log
    const el = card.querySelector('#cmp-sig');
    if (el) el.innerHTML = `<span style="${EYEBROW}color:var(--brand-orange);">Could not read your Gmail signature (${escapeHtml(why)})</span><span style="font-size:12.5px;color:var(--ink-2);">This will go with a plain name block instead.</span><div class="cmp-sig-paper">${fallbackSig}</div>`;
  });

  // ---- initial paint ------------------------------------------------------
  renderJobSlot();
  renderCats();
  renderSentAs();
  renderFiled();
  renderAttachments();

  // ---- wiring -------------------------------------------------------------
  const toEl = card.querySelector('#cmp-to');
  const subjEl = card.querySelector('#cmp-subject');
  toEl.addEventListener('input', () => { draft.to = toEl.value; });
  subjEl.addEventListener('input', () => { draft.subject = subjEl.value; renderSentAs(); });
  // "Nobody chases ASP" (08/10/2026): a holding phrase with no time asks for one.
  const bodyIn = card.querySelector('#cmp-body');
  if (bodyIn) bodyIn.addEventListener('input', () => {
    const h = holdingWithoutTime(htmlToText(bodyIn.innerHTML));
    if (h) showNote(`Add a time: “${h.phrase}” doesn’t say when — e.g. “by 3pm today”.`, 'var(--brand-orange)');
    else if (/Add a time:/.test(noteEl.textContent)) showNote('');
  });

  const ccRow = card.querySelector('#cmp-ccrow');
  card.querySelector('#cmp-ccbcc').addEventListener('click', () => {
    ccRow.style.display = ccRow.style.display === 'none' ? 'flex' : 'none';
  });
  card.querySelector('#cmp-cc').addEventListener('input', (e) => { draft.cc = e.target.value; });
  card.querySelector('#cmp-bcc').addEventListener('input', (e) => { draft.bcc = e.target.value; });

  // ---- SEND -----------------------------------------------------------------
  const sendBtn = card.querySelector('#cmp-send');
  let sending = false;
  sendBtn.addEventListener('click', async () => {
    if (sending) return;
    const to = parseAddresses(draft.to);
    const cc = parseAddresses(draft.cc);
    const bcc = parseAddresses(draft.bcc);
    if (!to.ok || !to.list.length) { showNote(to.bad ? `“${to.bad}” is not an email address.` : 'Add who it is to.', 'var(--brand-orange)'); return; }
    if (!cc.ok || !bcc.ok) { showNote(`“${cc.bad || bcc.bad}” is not an email address.`, 'var(--brand-orange)'); return; }
    if (!String(draft.subject || '').trim()) { showNote('Add a subject.', 'var(--brand-orange)'); return; }
    { const b = card.querySelector('#cmp-body'); if (b && /\[(time|day|date|day\/date)\]/i.test(htmlToText(b.innerHTML))) { showNote('Fill in the [time] first — a holding reply needs a firm time.', 'var(--brand-orange)'); return; } }
    if (!draft.job && !window.confirm('This has no project, so it will not be filed. Send anyway?')) return;
    sending = true;
    const was = sendBtn.textContent;
    sendBtn.textContent = 'Sending…';
    try {
      // The permission first, while this click still counts as one: Google's
      // consent popup is blocked once the page has spent seconds downloading.
      await ensureScopedToken('https://www.googleapis.com/auth/gmail.send');
      // The bytes: device files from the picker, project files from Drive.
      const files = [];
      for (const a of draft.attachments) {
        if (a.kind === 'device' && a.file) files.push({ name: a.name, mimeType: a.file.type || 'application/octet-stream', bytes: new Uint8Array(await a.file.arrayBuffer()) });
        else if (a.kind === 'project' && a.fileId) files.push({ name: a.name, mimeType: a.mimeType || 'application/octet-stream', bytes: new Uint8Array(await downloadDriveFile(a.fileId)) });
      }
      const total = files.reduce((n, f) => n + f.bytes.length, 0);
      if (total > MAX_ATTACH_BYTES) throw new Error(`The attachments come to ${(total / 1048576).toFixed(1)} MB; Gmail takes 25 MB. Send fewer, or share them from Drive.`);
      // The signature sits above any quoted message, as Gmail places it.
      const bodyEl = card.querySelector('#cmp-body');
      const work = document.createElement('div');
      work.innerHTML = bodyEl ? bodyEl.innerHTML : '';
      const sigBlock = document.createElement('div');
      sigBlock.className = 'gmail_signature';
      sigBlock.innerHTML = sigHtml || fallbackSig;
      const quote = work.querySelector('[data-quote]');
      if (quote) {
        work.insertBefore(sigBlock, quote);
        // Track's theme variables mean nothing in the recipient's mail client.
        quote.setAttribute('style', 'color:rgb(90,90,90);border-left:2px solid rgb(204,204,204);padding-left:12px;margin-top:8px;');
      } else work.appendChild(sigBlock);
      const raw = work.innerHTML;
      const safe = window.DOMPurify ? window.DOMPurify.sanitize(raw) : escapeHtml(htmlToText(raw)).replace(/\n/g, '<br>');
      const subject = sentSubject(draft.subject, draft.job);
      const mime = buildMime({
        to: to.list, cc: cc.list, bcc: bcc.list, subject,
        html: `<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.5;">${safe}</div>`,
        inReplyTo: replyCtx.inReplyTo, references: replyCtx.references, attachments: files,
      });
      const sent = await sendMessage(mime, { threadId: replyCtx.threadId });
      // Gmail has it — that is the send. Close now; filing to the project (a
      // server call that may be waking up) finishes in the background, so the
      // window no longer waits seconds on it (07/10/2026).
      close();
      const t = document.createElement('div');
      t.setAttribute('role', 'status');
      t.style.cssText = 'position:fixed;left:50%;bottom:28px;transform:translateX(-50%);z-index:300;background:var(--ink-fill);color:var(--ink-fill-ink);padding:11px 18px;border-radius:10px;font-size:13px;';
      t.textContent = draft.job ? `Sent. Filing to ${draft.job}\u2026` : 'Sent.';
      document.body.appendChild(t);
      const done = (msg) => { t.textContent = msg; setTimeout(() => t.remove(), 3500); };
      if (draft.job && sent && sent.threadId) {
        callFunction('mailFile', {
          projectNo: draft.job, threadId: sent.threadId, subject, from: (session && session.email) || '',
          to: to.list.join(', '), body: htmlToText(safe).slice(0, 2000), date: new Date().toISOString(),
          attachmentCount: files.length,
        }).then(() => done(`Sent, and filed to ${draft.job}.`))
          // Sent is sent; the TRACK// tag still files it via the archiver.
          .catch(() => done(`Sent. Filing to ${draft.job} will follow from the TRACK// tag.`));
      } else done('Sent.');
    } catch (e) {
      sending = false;
      sendBtn.textContent = was;
      logError('mail.send', (e && e.message) || 'send failed', e && e.stack);
      const m = (e && e.message) || '';
      showNote(/load failed|failed to fetch|network/i.test(m)
        ? `Gmail could not be reached from this browser (${m}). Nothing was sent — try again; if it repeats, Track has logged it.`
        : (m || 'Gmail would not send it.'), 'var(--brand-orange)');
    }
  });

  // Attach from this device: no upload since send is off — chosen files are held
  // on the draft as display-only chips (bytes are read only when send lands).
  const deviceInput = card.querySelector('#cmp-device');
  card.querySelector('#cmp-attach-dev').addEventListener('click', () => deviceInput.click());
  deviceInput.addEventListener('change', () => {
    Array.from(deviceInput.files || []).forEach((f) => addAttachment({ kind: 'device', name: f.name, size: f.size, file: f }));
    deviceInput.value = '';
    renderAttachments();
  });

  // Attach from project → the 2c picker (needs a resolved job + folderId).
  card.querySelector('#cmp-attach-proj').addEventListener('click', () => {
    if (!draft.job || !draft.project) { showNote('Add a project number first — the picker opens on that project’s files.', 'var(--brand-orange)'); return; }
    // No folder linked on the project (a third of them) → find it by number,
    // as Files does (drive-folders.js pickProjectFolder), before giving up.
    openProjectPicker(draft.job, draft.project, (picked) => {
      picked.forEach(addAttachment);
      renderAttachments();
      showNote(`Added ${picked.length} file${picked.length === 1 ? '' : 's'} from ${draft.job}.`, 'var(--brand-blue)');
    });
  });

  // ---- ASK FOR APPROVAL (Karan 07/10/2026) ----------------------------------
  // Another person reads it, suggests changes (tracked) and approves it before
  // it goes. The request is a record (mailApprovals); nothing is sent here. If
  // "Send as soon as it's approved" is ticked, the server sends it from this
  // person's own Gmail on approval — unless suggestions are waiting for them.
  const askBtn = card.querySelector('#cmp-ask');
  const askPanel = card.querySelector('#cmp-askpanel');
  const DEVICE_LIMIT = 700 * 1024;
  const toBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = () => reject(new Error(`Could not read ${file.name}`));
    r.readAsDataURL(file);
  });
  const openAsk = async () => {
    if (!askPanel.hidden) { askPanel.hidden = true; return; }
    const to = parseAddresses(draft.to);
    if (!to.ok || !to.list.length) { showNote(to.bad ? `“${to.bad}” is not an email address.` : 'Add who it is to before asking for approval.', 'var(--brand-orange)'); return; }
    if (!String(draft.subject || '').trim()) { showNote('Add a subject before asking for approval.', 'var(--brand-orange)'); return; }
    const deviceBytes = draft.attachments.filter((a) => a.kind === 'device').reduce((n, a) => n + (a.size || 0), 0);
    if (deviceBytes > DEVICE_LIMIT) {
      showNote(`Files from this computer come to ${(deviceBytes / 1024).toFixed(0)} KB; an approval can carry 700 KB. Attach them from the project instead, or send without approval.`, 'var(--brand-orange)');
      return;
    }
    askPanel.hidden = false;
    askPanel.innerHTML = '<span style="font-size:12.5px;color:var(--ink-3);">Loading the team…</span>';
    const me = String((session && session.email) || '').toLowerCase();
    const people = (await allUsers().catch(() => []))
      .filter((u) => u.active !== false && u.email && String(u.email).toLowerCase() !== me && isPerson(u))
      .map((u) => ({ email: String(u.email).toLowerCase(), name: u.displayName || String(u.email).split('@')[0] }))
      .sort((a, b) => a.name.localeCompare(b.name));
    askPanel.innerHTML = `
      <span style="${EYEBROW}color:var(--ink-3);">Ask for approval</span>
      <div class="cmp-ask-row">
        <label class="cmp-ask-field"><span>Who approves it</span>
          <select id="cmp-reviewer" aria-label="Who approves it"><option value="">Choose a person…</option>${people.map((p) => `<option value="${escapeHtml(p.email)}">${escapeHtml(p.name)}</option>`).join('')}</select></label>
        <label class="cmp-ask-field" style="flex:1;"><span>A note for them (optional)</span>
          <input id="cmp-asknote" type="text" maxlength="500" placeholder="e.g. Check the fee wording before it goes" aria-label="A note for the reviewer"></label>
      </div>
      <label class="cmp-ask-auto"><input id="cmp-autosend" type="checkbox"> Send as soon as it’s approved <span style="color:var(--ink-3);">— from your Gmail, unless they have suggested changes for you to decide</span></label>
      <div style="display:flex;gap:10px;align-items:center;">
        <button type="button" id="cmp-ask-go" class="cmp-ask-go">Send for approval</button>
        <button type="button" id="cmp-ask-cancel" class="cmp-ask-cancel">Cancel</button>
      </div>`;
    askPanel.querySelector('#cmp-ask-cancel').addEventListener('click', () => { askPanel.hidden = true; });
    askPanel.querySelector('#cmp-ask-go').addEventListener('click', async (ev) => {
      const go = ev.currentTarget;
      const reviewerEmail = askPanel.querySelector('#cmp-reviewer').value;
      const reviewer = people.find((p) => p.email === reviewerEmail);
      if (!reviewer) { showNote('Choose who approves it.', 'var(--brand-orange)'); return; }
      go.disabled = true; go.textContent = 'Sending for approval…';
      try {
        // The body as plain text (suggestions are tracked on words); the quoted
        // earlier message rides beside it, untouched, with email-safe styles.
        const bodyEl = card.querySelector('#cmp-body');
        const work = document.createElement('div');
        work.innerHTML = bodyEl ? bodyEl.innerHTML : '';
        const quote = work.querySelector('[data-quote]');
        let quoteHtml = '';
        if (quote) {
          quote.remove();
          quote.setAttribute('style', 'color:rgb(90,90,90);border-left:2px solid rgb(204,204,204);padding-left:12px;margin-top:8px;');
          quoteHtml = window.DOMPurify ? window.DOMPurify.sanitize(quote.outerHTML) : '';
        }
        const text = htmlToText(window.DOMPurify ? window.DOMPurify.sanitize(work.innerHTML) : work.textContent || '');
        const files = []; const attachments = [];
        for (const a of draft.attachments) {
          if (a.kind === 'project' && a.fileId) attachments.push({ kind: 'project', name: a.name, fileId: a.fileId, mimeType: a.mimeType || '' });
          else if (a.kind === 'device' && a.file) {
            attachments.push({ kind: 'device', name: a.name, mimeType: a.file.type || 'application/octet-stream', size: a.size || 0, file: `files/${files.length}` });
            files.push({ name: a.name, mimeType: a.file.type || 'application/octet-stream', b64: await toBase64(a.file) });
          }
        }
        await createMailApproval({
          authorEmail: me, authorName: (session && session.displayName) || me,
          reviewerEmail: reviewer.email, reviewerName: reviewer.name,
          note: askPanel.querySelector('#cmp-asknote').value.trim().slice(0, 500),
          autoSend: !!askPanel.querySelector('#cmp-autosend').checked,
          status: 'pending', suggestions: [], comments: [],
          draft: {
            to: to.list, cc: parseAddresses(draft.cc).list, bcc: parseAddresses(draft.bcc).list,
            subject: String(draft.subject || '').trim(), text, quoteHtml,
            sigHtml: sigHtml || fallbackSig,
            project: draft.job ? { number: draft.job, id: (draft.project && draft.project.id) || null } : null,
            threadId: replyCtx.threadId || '', inReplyTo: replyCtx.inReplyTo || '', references: replyCtx.references || '',
            attachments,
          },
        }, files);
        close();
        const t = document.createElement('div');
        t.setAttribute('role', 'status');
        t.style.cssText = 'position:fixed;left:50%;bottom:28px;transform:translateX(-50%);z-index:300;background:var(--ink-fill);color:var(--ink-fill-ink);padding:11px 18px;border-radius:10px;font-size:13px;';
        t.textContent = `Sent to ${reviewer.name} for approval.`;
        document.body.appendChild(t);
        setTimeout(() => t.remove(), 3500);
      } catch (e) {
        go.disabled = false; go.textContent = 'Send for approval';
        showNote((e && e.message) || 'The request did not save.', 'var(--brand-orange)');
      }
    });
  };
  askBtn.addEventListener('click', openAsk);
  askBtn.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAsk(); } });

  return { close };
}

// Build the quoted reply/forward body seeded into the contenteditable area.
function quotedBodyHtml(mode, thread) {
  if ((mode !== 'reply' && mode !== 'forward') || !Array.isArray(thread) || !thread.length) return '';
  const m = thread[thread.length - 1]; // newest
  const head = mode === 'forward'
    ? '---------- Forwarded message ----------'
    : `On ${whenFull(m.date)}, ${senderNameOf(m)} wrote:`;
  const text = (m.text && m.text.trim()) ? m.text : (m.snippet || '');
  return `<div><br></div>
    <div data-quote="1" style="color:var(--ink-3);border-left:2px solid var(--hair);padding-left:12px;margin-top:8px;">
      <div style="font-size:12.5px;">${escapeHtml(head)}</div>
      <div style="white-space:pre-wrap;font-size:13.5px;margin-top:6px;">${escapeHtml(text)}</div>
    </div>`;
}

// ===========================================================================
// ATTACH-FROM-PROJECT PICKER (2c right panel, 560px)
// ===========================================================================
// onAttach receives an array of { kind:'project', name, docNumber } references.
function openProjectPicker(job, project, onAttach) {
  const overlay = document.createElement('div');
  overlay.setAttribute('style', PICKER_BACKDROP);
  document.body.appendChild(overlay);
  const close = () => { overlay.remove(); document.removeEventListener('keydown', onKey, true); };
  function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); close(); } }
  document.addEventListener('keydown', onKey, true);
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });

  const CARD = 'width:560px;max-width:100%;background:var(--surface);border:1px solid var(--hair);border-radius:16px;box-shadow:0 22px 50px rgba(12,17,57,0.18);overflow:hidden;display:flex;flex-direction:column;';

  overlay.innerHTML = `<div style="${CARD}"><div style="padding:40px 24px;color:var(--ink-3);font-size:13px;">Reading ${escapeHtml(String(job))}’s files…</div></div>`;

  // ---- load the project's files (code subfolders → their files) -----------
  (async () => {
    let files = [];
    try {
      let folderId = project.folderId;
      if (!folderId) {
        const hit = pickProjectFolder(job, await searchProjectFolders(job).catch(() => []));
        folderId = hit.folder && hit.folder.id;
      }
      if (!folderId) throw new Error('nofolder');
      const top = await listFolder(folderId);
      const codeFolders = top.filter((f) => isFolderMime(f.mimeType) && folderCode(f.name));
      // Into the subfolders too (08/10/2026): most files sit a level or two down
      // ("4. Calculations / Working Calculations"), so a project full of files
      // read as "no files in its Drive folders yet". Superseded copies are left out.
      const listed = await Promise.all(codeFolders.map((cf) =>
        walkFolder(listFolder, cf.id, { maxDepth: 3, maxFolders: 40 }).then((w) => w.files
          .filter((k) => !isFolderMime(k.mimeType) && !isSupersededPath(k.path || []))
          .map((k) => ({ ...k, _folder: folderCode(cf.name), _folderHuman: [folderHuman(cf.name), ...(k.path || [])].join(' / ') })))
          .catch(() => [])));
      files = listed.flat();
    } catch (_) { files = []; }

    if (!files.length) {
      overlay.innerHTML = `<div style="${CARD}">
        <div style="padding:34px 24px;display:flex;flex-direction:column;gap:14px;">
          <span style="${EYEBROW}color:var(--brand-orange);">Nothing to attach</span>
          <span style="font-size:14px;color:var(--ink-2);line-height:1.6;">${escapeHtml(String(job))} has no files in its Drive folders yet.</span>
          <div><span id="pk-close" style="border:1px solid var(--hair-strong);border-radius:8px;padding:10px 16px;font-size:13px;cursor:pointer;display:inline-block;">Close</span></div>
        </div></div>`;
      overlay.querySelector('#pk-close').addEventListener('click', close);
      return;
    }

    // Decorate each file with its parsed number + attach status.
    // OPEN DECISION #1: no Issued/Draft status source on Drive; PDFs treated as
    // issued, others require Confirm. Confirm the real status model with Karan.
    const rows = files.map((f, i) => {
      const parsed = parseDocNumber(f.name);
      const isCad = CAD_EXT.test(f.name);
      const isPdf = PDF_EXT.test(f.name) || f.mimeType === 'application/pdf';
      // Rule 1 — drawings attach as the issued PDF, not the CAD file: a CAD file
      // is never offered as attachable (shown greyed); its .pdf sibling is.
      let status;
      if (isCad) status = 'cad';
      else if (isPdf) status = 'issued';
      else status = 'draft'; // Rule 2 — needs Confirm before it can be ticked.
      const number = parsed ? `${parsed.project}-${parsed.folder}-${parsed.type}${parsed.item}${parsed.rev ? `_${parsed.rev}` : ''}` : stemOf(f.name);
      return {
        i, id: f.id, name: f.name, mimeType: f.mimeType || "", folder: f._folder, folderHuman: f._folderHuman,
        modifiedTime: f.modifiedTime || '', number, status, isPdf, isCad,
        ext: extOf(f.name).replace('.', '').toUpperCase() || 'FILE',
      };
    });

    // ---- live state -------------------------------------------------------
    let activePill = 'Issued'; // 2c opens with Issued active.
    let query = '';
    const ticked = new Set();    // row ids
    const confirmed = new Set(); // draft row ids the user has OK'd to send

    const PILLS = ['Issued', 'Drawings', 'Calculations', 'Recent'];

    function visibleRows() {
      let list = rows.slice();
      // Filter pill scope.
      if (activePill === 'Issued') list = list.filter((r) => r.status === 'issued');
      else if (activePill === 'Drawings') list = list.filter((r) => r.folder === '05');
      else if (activePill === 'Calculations') list = list.filter((r) => r.folder === '04');
      else if (activePill === 'Recent') list = list.slice().sort((a, b) => String(b.modifiedTime).localeCompare(String(a.modifiedTime))).slice(0, 12);
      // Search: number, name or revision.
      const q = query.trim().toLowerCase();
      if (q) list = list.filter((r) => `${r.number} ${r.name}`.toLowerCase().includes(q));
      return list;
    }

    function rowHtml(r) {
      const isTicked = ticked.has(r.id);
      const attachable = r.status !== 'cad';
      // Box: navy check when ticked, hairline square otherwise.
      const box = isTicked
        ? '<span style="width:17px;height:17px;border-radius:4px;background:var(--ink-fill);color:var(--ink-fill-ink);display:flex;align-items:center;justify-content:center;font-size:11px;flex:none;">&#10003;</span>'
        : `<span style="width:17px;height:17px;border-radius:4px;border:1.5px solid var(--hair-strong);flex:none;${attachable ? '' : 'opacity:0.4;'}"></span>`;
      let metaColor = 'var(--ink-2)';
      let metaText;
      let right;
      if (r.status === 'cad') {
        metaColor = 'var(--ink-3)'; metaText = `${r.number} · CAD file — the issued PDF is sent instead`;
        right = '<span style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--ink-3);flex:none;">CAD</span>';
      } else if (r.status === 'draft') {
        metaColor = 'var(--brand-orange)'; metaText = `${r.number} · draft, never issued`;
        right = confirmed.has(r.id)
          ? '<span style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--link);flex:none;">Confirmed</span>'
          : `<span class="pk-confirm" data-id="${escapeHtml(r.id)}" style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--brand-orange);flex:none;cursor:pointer;">Confirm</span>`;
      } else {
        metaText = `${r.number} · issued`;
        right = `<span style="font-size:11px;letter-spacing:0.08em;text-transform:uppercase;color:var(--ink-3);flex:none;">${escapeHtml(r.ext)}</span>`;
      }
      const bg = isTicked ? 'background:var(--halo);' : '';
      const nameColor = attachable && r.status !== 'draft' ? '' : 'color:var(--ink-2);';
      const rowCursor = attachable ? 'cursor:pointer;' : 'cursor:default;opacity:0.55;';
      return `<div class="pk-row" data-id="${escapeHtml(r.id)}" style="padding:13px 22px;border-bottom:1px solid var(--hair);display:flex;align-items:center;gap:13px;${bg}${rowCursor}">
        ${box}
        <span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:3px;">
          <span style="font-size:13.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;${nameColor}">${escapeHtml(fileTitle(r))}</span>
          <span style="${MONO}font-size:11.5px;color:${metaColor};">${escapeHtml(metaText)}</span>
        </span>
        ${right}
      </div>`;
    }

    function paint() {
      const pillsHtml = PILLS.map((p) => {
        const on = p === activePill;
        const style = on
          ? 'background:var(--ink-fill);color:var(--ink-fill-ink);border:1px solid var(--ink);'
          : 'border:1px solid var(--hair);color:var(--ink);';
        return `<span class="pk-pill" data-pill="${escapeHtml(p)}" style="${style}border-radius:999px;padding:5px 12px;font-size:11.5px;cursor:pointer;">${escapeHtml(p)}</span>`;
      }).join('');
      const list = visibleRows();
      const listHtml = list.length
        ? list.map(rowHtml).join('')
        : '<div style="padding:26px 22px;color:var(--ink-3);font-size:13px;">Nothing matches those filters.</div>';
      const n = ticked.size;
      overlay.innerHTML = `<div style="${CARD}">
        <div style="padding:20px 22px 14px;border-bottom:1px solid var(--hair);display:flex;flex-direction:column;gap:10px;">
          <span style="${EYEBROW}color:var(--brand-orange);">Attach from <span style="${MONO}">${escapeHtml(String(job))}</span></span>
          <span style="border:1px solid var(--hair);border-radius:8px;padding:9px 12px;display:flex;align-items:center;gap:8px;">
            <input id="pk-search" type="text" value="${escapeHtml(query)}" placeholder="Number, name or revision" style="border:none;background:transparent;flex:1;font-size:13px;color:var(--ink);" />
            <span style="${MONO}font-size:11px;color:var(--ink-3);">/</span>
          </span>
          <div style="display:flex;gap:7px;flex-wrap:wrap;">${pillsHtml}</div>
        </div>
        <div id="pk-list" style="display:flex;flex-direction:column;max-height:360px;overflow:auto;">${listHtml}</div>
        <div style="padding:16px 22px;background:var(--brief-bg);border-top:1px solid var(--hair);display:flex;align-items:center;gap:12px;">
          <span id="pk-attach" style="background:var(--ink-fill);color:var(--ink-fill-ink);border-radius:8px;padding:10px 18px;font-size:13px;font-weight:500;cursor:pointer;${n ? '' : 'opacity:0.5;'}">Attach ${n} file${n === 1 ? '' : 's'}</span>
          <span style="font-size:12px;color:var(--ink-2);line-height:1.45;">Sends the issued PDF, not the CAD file.</span>
        </div>
      </div>`;

      // wire search (preserve caret position by re-focusing at end)
      const search = overlay.querySelector('#pk-search');
      search.addEventListener('input', () => { query = search.value; paint(); });
      // keep focus after repaint when the user was typing
      if (query) { search.focus(); search.setSelectionRange(query.length, query.length); }

      overlay.querySelectorAll('.pk-pill').forEach((el) => el.addEventListener('click', () => { activePill = el.dataset.pill; paint(); }));

      // Confirm a draft before it can be ticked (stopPropagation so the row's own
      // toggle doesn't also fire).
      overlay.querySelectorAll('.pk-confirm').forEach((el) => el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        confirmed.add(el.dataset.id);
        paint();
      }));

      overlay.querySelectorAll('.pk-row').forEach((el) => el.addEventListener('click', () => {
        const r = rows.find((x) => x.id === el.dataset.id);
        if (!r || r.status === 'cad') return;            // CAD never attachable (Rule 1)
        if (r.status === 'draft' && !confirmed.has(r.id)) return; // must Confirm first (Rule 2)
        if (ticked.has(r.id)) ticked.delete(r.id); else ticked.add(r.id);
        paint();
      }));

      overlay.querySelector('#pk-attach').addEventListener('click', () => {
        // The Drive id travels with the chip; Send downloads the bytes then.
        const picked = rows.filter((r) => ticked.has(r.id)).map((r) => ({ kind: 'project', name: r.name, docNumber: r.number, fileId: r.id, mimeType: r.mimeType || '' }));
        if (picked.length && typeof onAttach === 'function') onAttach(picked);
        close();
      });
    }

    paint();
  })();
}

// A human title for a file row: the parsed detail if present, else the stem
// with the document number folded out for readability.
function fileTitle(r) {
  const stem = stemOf(r.name);
  // If the stem is exactly the document number, fall back to the folder's human
  // name so the row isn't just the number twice.
  if (stem === r.number) return r.folderHuman || stem;
  // Strip a leading number token so "7014-05-0301_C Foundation GA" reads clean.
  const rest = stem.replace(/^(\d{4}|A\d{4})-\d{2}-\d{4}(?:_[A-Za-z]\d*)?[\s._-]*/, '').trim();
  return rest || stem;
}
